import {
  Box,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Spacer,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import AppButton from "@/features/line-sheet-sets/app-button";
import PriceAdjustedByTypeSelect from "@/features/order-sheet-sets/price-adjusted-by-type-select";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useIdentity from "@/features/ui/hooks/use-identity";
import useListLineSheets from "@/features/line-sheet-sets/hooks/use-list-line-sheets";
import useParseOrderRulesToConditions, {
  ParseOrderRulesToConditionsBrand,
  ParseOrderRulesToConditionsResult,
  formatMatchedConditionLabel,
} from "@/features/line-sheet-sets/hooks/use-parse-order-rules-to-conditions";
import useGetParseOrderRulesJob from "@/features/line-sheet-sets/hooks/use-get-parse-order-rules-job";
import useResolveProductConditions from "@/features/line-sheet-sets/hooks/use-resolve-product-conditions";
import useGetInspectParsingJob from "@/features/line-sheet-sets/hooks/use-get-inspect-parsing-job";
import { PriceAdjustedBy } from "@/features/line-sheet-sets/line-sheet-set.types";
import SVG from "react-inlinesvg";
import { fileIconsByType } from "@/shared/constants";
import useDownloadUnoFile from "@/hooks/use-download-uno-file";
import ApplyConditionProductModal from "@/features/line-sheet-sets/apply-condition-product-modal";
import OrderRuleParseChoiceModal from "@/features/line-sheet-sets/order-rule-parse-choice-modal";
import useStoredOrderRuleParseResult, {
  StoredOrderRuleParseResult,
} from "@/features/line-sheet-sets/hooks/use-stored-order-rule-parse-result";

type ConditionBy = PriceAdjustedBy["by"];

// 매칭된 브랜드 컨디션을 inspect-parsing에 보낼 오더룰 텍스트로 변환.
// (apply-condition-modal.tsx의 동일 헬퍼와 같은 포맷)
function formatBrandConditionsAsText(
  brand: ParseOrderRulesToConditionsBrand
): string {
  if (!brand.conditions || brand.conditions.length === 0) return "";
  const lines = brand.conditions.map((c) => {
    const sub =
      c.subCategory && c.subCategory.trim() !== ""
        ? c.subCategory.trim()
        : "기본";
    const ivaSuffix = c.priceAdjustedBy.applyIva ? " (IVA 적용)" : "";
    return `- ${sub}: ${c.priceAdjustedBy.by} ${c.priceAdjustedBy.value}%${ivaSuffix}`;
  });
  return [`[${brand.brandName}]`, ...lines].join("\n");
}

export interface BrandConditionEntry {
  brandId: number;
  by: "MARKUP" | "DISCOUNT";
  value: string;
}

// 브랜드별 모달 안에서 상품별로 적용한 결과. 제출 시 line_sheet_product_id로 변환.
export interface BrandProductConditionEntry {
  brandId: number;
  productId: number;
  by: "MARKUP" | "DISCOUNT";
  value: string;
}

export interface ApplyConditionBrandResult {
  brandEntries: BrandConditionEntry[];
  productEntries: BrandProductConditionEntry[];
}

interface ConditionRow {
  brandId: number;
  brandName: string;
  by: ConditionBy;
  value: string;
}

interface ApplyConditionBrandModalProps {
  isOpen: boolean;
  onClose: () => void;
  lineSheetSetId: number;
  // 선택한 라인 시트 또는 시즌. 해당 대상에 속한 브랜드만 노출한다.
  target:
    | { kind: "LINE_SHEET"; id: number; name: string }
    | { kind: "SEASON"; seasonName: string; name: string };
  initialEntries?: Map<number, { by: "MARKUP" | "DISCOUNT"; value: string }>;
  // 브랜드별 상품 컨디션 복원용 — brandId -> (productId -> 컨디션)
  initialProductEntries?: Map<
    number,
    Map<number, { by: "MARKUP" | "DISCOUNT"; value: string }>
  >;
  orderRulesText?: string;
  orderRuleFiles?: { id: number; name: string }[];
  onConfirm: (result: ApplyConditionBrandResult) => void;
}

export default function ApplyConditionBrandModal({
  isOpen,
  onClose,
  lineSheetSetId,
  target,
  initialEntries,
  initialProductEntries,
  orderRulesText,
  orderRuleFiles,
  onConfirm,
}: ApplyConditionBrandModalProps) {
  const { tTitle } = useI18nHelper();
  const { error: showError, info: showInfo } = useAppToasts();
  const identity = useIdentity();

  const orderRuleFileList = orderRuleFiles ?? [];
  const hasOrderRuleContent =
    (orderRulesText?.trim() ?? "") !== "" || orderRuleFileList.length > 0;

  // line-sheets API로 LSS의 라인시트 목록을 받아, 선택한 라인 시트의 브랜드를 추출한다.
  const { data: lineSheetsData, isLoading } = useListLineSheets(
    identity?.company
      ? {
          by: identity.company.type,
          companyId: identity.company.id,
          lineSheetSetId,
        }
      : ({ by: "AGENCY", companyId: 0, lineSheetSetId } as const),
    { pageSize: 500, pageNumber: 1 }
  );

  const brands = useMemo(() => {
    const lineSheets = lineSheetsData?.lineSheets ?? [];
    if (target.kind === "LINE_SHEET") {
      return lineSheets.find((ls) => ls.id === target.id)?.brands ?? [];
    }
    // SEASON: 해당 시즌을 포함하는 라인시트들의 브랜드를 합쳐서 노출.
    const map = new Map<number, string>();
    lineSheets
      .filter((ls) => (ls.seasons ?? []).includes(target.seasonName))
      .forEach((ls) =>
        (ls.brands ?? []).forEach((b) => map.set(b.id, b.name))
      );
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [lineSheetsData, target]);

  const [rows, setRows] = useState<ConditionRow[]>([]);
  const [bulkBy, setBulkBy] = useState<ConditionBy>("MARKUP");
  const [bulkValue, setBulkValue] = useState<string>("");
  const [isOrderRulePanelOpen, setIsOrderRulePanelOpen] = useState(true);
  const { fire: fireDownloadUnoFile } = useDownloadUnoFile();
  // 파싱된 brand_id별 매칭 룰 — 행 칩 표시 및 반영에 사용.
  const [matchedRulesByBrandId, setMatchedRulesByBrandId] = useState<
    Map<number, ParseOrderRulesToConditionsBrand>
  >(new Map());
  // 브랜드별 "상품별 적용" 결과. brandId -> (productId -> 컨디션)
  const [productModalBrand, setProductModalBrand] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [productConditionMap, setProductConditionMap] = useState<
    Map<number, Map<number, { by: "MARKUP" | "DISCOUNT"; value: string }>>
  >(new Map());

  const { fire: fireParseOrderRules } = useParseOrderRulesToConditions();
  const { fire: fireGetParseOrderRulesJob } = useGetParseOrderRulesJob();
  const { fire: fireResolveProductConditions } = useResolveProductConditions();
  const { fire: fireGetInspectParsingJob } = useGetInspectParsingJob();
  const [isParsing, setIsParsing] = useState(false);
  const [isReflecting, setIsReflecting] = useState(false);
  // 오더룰 파일이 여러개일 때 어떤 파일로 파싱할지 고르는 모달 상태
  const [isFileSelectOpen, setIsFileSelectOpen] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState<number | null>(null);
  // 저장된 파싱 결과(localStorage, 파일별) — 메인 모달과 동일 키로 공유.
  const { get: getStoredParseResult, save: saveParseResult } =
    useStoredOrderRuleParseResult(lineSheetSetId);
  const [isParseChoiceOpen, setIsParseChoiceOpen] = useState(false);
  const [parseChoiceFileId, setParseChoiceFileId] = useState<number | null>(
    null
  );
  const [parseChoiceStored, setParseChoiceStored] =
    useState<StoredOrderRuleParseResult | null>(null);

  useEffect(() => {
    if (isOpen) {
      setBulkBy("MARKUP");
      setBulkValue("");
      setMatchedRulesByBrandId(new Map());
      // 부모가 넘긴 상품 컨디션으로 복원(깊은 복사).
      const restored = new Map<
        number,
        Map<number, { by: "MARKUP" | "DISCOUNT"; value: string }>
      >();
      initialProductEntries?.forEach((sub, brandId) => {
        restored.set(brandId, new Map(sub));
      });
      setProductConditionMap(restored);
    }
  }, [isOpen, initialProductEntries]);

  // 브랜드 목록(또는 initialEntries)이 준비되면 행을 구성한다.
  useEffect(() => {
    if (!isOpen) return;
    setRows(
      brands.map((b) => {
        const existing = initialEntries?.get(b.id);
        return {
          brandId: b.id,
          brandName: b.name,
          by: existing?.by ?? "MARKUP",
          value: existing?.value ?? "",
        };
      })
    );
  }, [isOpen, brands, initialEntries]);

  const handleChangeRow = (index: number, patch: Partial<ConditionRow>) => {
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r))
    );
  };

  const handleApplyToAll = () => {
    setRows((prev) => prev.map((r) => ({ ...r, by: bulkBy, value: bulkValue })));
  };

  // 파일(또는 텍스트)에 저장된 결과가 있으면 선택 모달, 없으면 곧바로 파싱.
  const maybeShowChoiceOrParse = (fileId: number | null) => {
    const s = getStoredParseResult(fileId);
    if (s) {
      setParseChoiceFileId(fileId);
      setParseChoiceStored(s);
      setIsParseChoiceOpen(true);
    } else {
      void handleParseOrderRules(fileId ?? undefined);
    }
  };

  // 파싱 버튼: 파일이 여러개면 파일 선택 먼저, 0~1개면 해당 파일 기준으로 분기.
  const handleClickParseButton = () => {
    if (isParsing) return;
    if (orderRuleFileList.length > 1) {
      setSelectedFileId(orderRuleFileList[0].id);
      setIsFileSelectOpen(true);
      return;
    }
    const fileId =
      orderRuleFileList.length === 1 ? orderRuleFileList[0].id : null;
    maybeShowChoiceOrParse(fileId);
  };

  // 저장된 파싱 결과를 API 호출 없이 그대로 적용(칩만 채움).
  const applyStoredParseResult = () => {
    if (!parseChoiceStored) return;
    const brandMap = new Map<number, ParseOrderRulesToConditionsBrand>();
    parseChoiceStored.result.brands.forEach((b) => brandMap.set(b.brandId, b));
    setMatchedRulesByBrandId(new Map(brandMap));
    setIsParseChoiceOpen(false);
    const brandIdsInLineSheet = new Set(brands.map((b) => b.id));
    const matchedInLineSheet = parseChoiceStored.result.brands.filter((b) =>
      brandIdsInLineSheet.has(b.brandId)
    ).length;
    showInfo(`저장된 파싱 결과 적용 (${matchedInLineSheet}개 브랜드)`);
  };

  // [1단계] 오더룰 파싱: 결과(브랜드별 매칭 룰)를 받아 칩으로 표시.
  const handleParseOrderRules = async (unoFileId?: number) => {
    if (isParsing) return;
    setIsParsing(true);
    try {
      const startRes = await fireParseOrderRules(
        { lineSheetSetId },
        unoFileId !== undefined ? { unoFileId } : {}
      );
      if (!startRes?.jobId) return;

      const PARSE_POLL_INTERVAL_MS = 2000;
      const PARSE_MAX_POLL_ATTEMPTS = 90; // 최대 3분
      let res: ParseOrderRulesToConditionsResult | undefined;
      for (let i = 0; i < PARSE_MAX_POLL_ATTEMPTS; i++) {
        await new Promise((resolve) =>
          setTimeout(resolve, PARSE_POLL_INTERVAL_MS)
        );
        const statusRes = await fireGetParseOrderRulesJob(
          { jobId: startRes.jobId },
          {}
        );
        const status = (statusRes?.status ?? "").toLowerCase();
        if (status === "completed" || status === "succeeded") {
          res = statusRes?.result ?? { brands: [], unmatched: [] };
          break;
        }
        if (status === "failed" || status === "error") {
          throw new Error(statusRes?.error || "오더룰 파싱 작업에 실패했습니다.");
        }
      }
      if (!res) {
        throw new Error("오더룰 파싱 작업이 시간 내에 완료되지 않았습니다.");
      }

      const brandMap = new Map<number, ParseOrderRulesToConditionsBrand>();
      res.brands.forEach((b) => brandMap.set(b.brandId, b));
      setMatchedRulesByBrandId(new Map(brandMap));
      // 파싱 결과를 localStorage에 저장 — 다음에 "저장된 결과 적용"으로 재사용.
      saveParseResult(res, unoFileId ?? null);

      // 이 라인 시트에 속한 브랜드 중 매칭된 개수만 집계해 안내.
      const brandIdsInLineSheet = new Set(brands.map((b) => b.id));
      const matchedInLineSheet = res.brands.filter((b) =>
        brandIdsInLineSheet.has(b.brandId)
      ).length;
      showInfo(`${matchedInLineSheet}개 브랜드 파싱 완료`);
    } catch (e) {
      showError(e);
    } finally {
      setIsParsing(false);
    }
  };

  // [2단계] 반영: 파싱된 매칭 룰을 적용.
  //  - 단일 컨디션 브랜드는 행에 값으로 즉시 반영.
  //  - 다중 컨디션 브랜드는 inspect-parsing(브랜드+라인시트 스코프)으로
  //    상품별 컨디션을 해석해 productConditionMap에 채운다.
  const handleReflect = async () => {
    if (isReflecting) return;
    if (matchedRulesByBrandId.size === 0) return;
    setIsReflecting(true);
    try {
      let appliedCount = 0;
      const multiRuleBrands: {
        id: number;
        name: string;
        brand: ParseOrderRulesToConditionsBrand;
      }[] = [];
      const nextRows = rows.map((row) => {
        const match = matchedRulesByBrandId.get(row.brandId);
        if (!match || match.conditions.length === 0) return row;
        if (match.conditions.length === 1) {
          const c = match.conditions[0];
          appliedCount++;
          return {
            ...row,
            by: c.priceAdjustedBy.by,
            value: String(c.priceAdjustedBy.value),
          };
        }
        multiRuleBrands.push({
          id: row.brandId,
          name: row.brandName,
          brand: match,
        });
        return row;
      });
      setRows(nextRows);

      const multiRuleSuccesses: {
        brandId: number;
        conditions: Record<
          string,
          { by: "MARKUP" | "DISCOUNT"; value: number; applyIva: boolean | null }
        >;
      }[] = [];
      const multiRuleFailedBrands: string[] = [];

      if (multiRuleBrands.length > 0) {
        const POLL_INTERVAL_MS = 2000;
        const MAX_POLL_ATTEMPTS = 60;
        // 서버 과부하 방지 — 동시 in-flight 최대 N개.
        const MAX_CONCURRENCY = 10;

        type PerBrandResult = {
          brandId: number;
          name: string;
          conditions: any;
        };

        const processBrand = async (
          item: (typeof multiRuleBrands)[number]
        ): Promise<PerBrandResult> => {
          const orderRulesTextForBrand = formatBrandConditionsAsText(
            item.brand
          );
          try {
            const startRes = await fireResolveProductConditions(
              { lineSheetSetId },
              {
                brandId: item.id,
                // 대상(라인 시트/시즌) 안의 해당 브랜드 상품으로 스코프.
                ...(target.kind === "LINE_SHEET"
                  ? { lineSheetId: target.id }
                  : { season: target.seasonName }),
                ...(orderRulesTextForBrand
                  ? { orderRulesText: orderRulesTextForBrand }
                  : {}),
              }
            );
            if (!startRes?.jobId) {
              return { brandId: item.id, name: item.name, conditions: null };
            }
            for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
              await new Promise((resolve) =>
                setTimeout(resolve, POLL_INTERVAL_MS)
              );
              const statusRes = await fireGetInspectParsingJob(
                { jobId: startRes.jobId },
                {}
              );
              const status = (statusRes?.status ?? "").toLowerCase();
              if (status === "completed" || status === "succeeded") {
                const result = statusRes?.result as any;
                const conditions =
                  result?.conditions ??
                  (result && typeof result === "object" ? result : null);
                return { brandId: item.id, name: item.name, conditions };
              }
              if (status === "failed" || status === "error") {
                return { brandId: item.id, name: item.name, conditions: null };
              }
            }
            return { brandId: item.id, name: item.name, conditions: null };
          } catch {
            return { brandId: item.id, name: item.name, conditions: null };
          }
        };

        // 슬라이딩 윈도우 워커 풀: 워커 N개가 공유 큐에서 작업을 뽑아 처리.
        const queue = [...multiRuleBrands];
        const resolvedPerBrand: PerBrandResult[] = [];
        const worker = async () => {
          while (queue.length > 0) {
            const item = queue.shift();
            if (!item) return;
            const result = await processBrand(item);
            resolvedPerBrand.push(result);
          }
        };
        await Promise.all(
          Array.from(
            { length: Math.min(MAX_CONCURRENCY, multiRuleBrands.length) },
            () => worker()
          )
        );

        for (const r of resolvedPerBrand) {
          if (r.conditions && typeof r.conditions === "object") {
            multiRuleSuccesses.push({
              brandId: r.brandId,
              conditions: r.conditions,
            });
          } else {
            multiRuleFailedBrands.push(r.name);
          }
        }

        if (multiRuleSuccesses.length > 0) {
          setProductConditionMap((prev) => {
            const next = new Map(prev);
            for (const { brandId, conditions } of multiRuleSuccesses) {
              const sub = new Map(next.get(brandId) ?? []);
              Object.entries(conditions).forEach(([pid, cond]) => {
                const productId = Number(pid);
                if (!Number.isFinite(productId)) return;
                sub.set(productId, {
                  by: cond.by,
                  value: String(cond.value),
                });
              });
              if (sub.size === 0) {
                next.delete(brandId);
              } else {
                next.set(brandId, sub);
              }
            }
            return next;
          });
        }
      }

      const multiAppliedTotalProducts = multiRuleSuccesses.reduce(
        (acc, s) => acc + Object.keys(s.conditions).length,
        0
      );

      const messages: string[] = [`${appliedCount}개 브랜드 자동 반영`];
      if (multiRuleBrands.length > 0) {
        messages.push(
          `다중 컨디션 ${multiRuleBrands.length}개 브랜드 → 상품별 자동 반영 ${multiAppliedTotalProducts}건`
        );
      }
      if (multiRuleFailedBrands.length > 0) {
        messages.push(
          `상품별 자동 반영 실패 ${multiRuleFailedBrands.length}건: ${multiRuleFailedBrands.join(", ")}`
        );
      }
      showInfo(messages.join("\n"));
    } catch (e) {
      showError(e);
    } finally {
      setIsReflecting(false);
    }
  };

  const handleConfirm = () => {
    const brandEntries: BrandConditionEntry[] = rows.map((r) => ({
      brandId: r.brandId,
      by: r.by === "DISCOUNT" ? "DISCOUNT" : "MARKUP",
      value: r.value,
    }));
    const productEntries: BrandProductConditionEntry[] = [];
    productConditionMap.forEach((sub, brandId) => {
      sub.forEach((cond, productId) => {
        productEntries.push({
          brandId,
          productId,
          by: cond.by,
          value: cond.value,
        });
      });
    });
    onConfirm({ brandEntries, productEntries });
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={"6xl"}
        blockScrollOnMount={!productModalBrand}
        trapFocus={!productModalBrand}
      >
        <ModalOverlay />
        <ModalContent overflow={"hidden"}>
          <ModalHeader
            color={"#444440"}
            fontSize={"14px"}
            background={"#F7F9FA"}
            borderBottom={"0.5px solid #8F8F8C"}
          >
            {tTitle("apply_order_rule")} - [
            {target.kind === "LINE_SHEET"
              ? tTitle("line_sheet")
              : tTitle("season")}
            ] {target.name}
          </ModalHeader>
          <ModalBody>
            <Flex gap={"12px"} alignItems={"stretch"}>
              {hasOrderRuleContent && (
                <Box
                  width={isOrderRulePanelOpen ? "240px" : "36px"}
                  flexShrink={0}
                  borderRadius={"6px"}
                  border={"0.5px solid #D4D4D1"}
                  background={"#FFF7CC"}
                  transition={"width 0.15s"}
                  overflow={"hidden"}
                  alignSelf={"flex-start"}
                >
                  <Flex
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    padding={"8px 10px"}
                    cursor={"pointer"}
                    onClick={() => setIsOrderRulePanelOpen((v) => !v)}
                    borderBottom={
                      isOrderRulePanelOpen ? "0.5px solid #E5DCA0" : "none"
                    }
                  >
                    {isOrderRulePanelOpen && (
                      <Text fontSize={"12px"} fontWeight={"700"}>
                        {tTitle("order_rule")}
                      </Text>
                    )}
                    <Box
                      transform={
                        isOrderRulePanelOpen ? "rotate(0deg)" : "rotate(180deg)"
                      }
                      transition={"transform 0.15s"}
                      color={"#8F8F8C"}
                      display={"flex"}
                    >
                      <SVG
                        src={"/icons-v1_1/chevron-double-left.svg"}
                        width={"14px"}
                        height={"14px"}
                      />
                    </Box>
                  </Flex>
                  {isOrderRulePanelOpen && (
                    <Box padding={"8px 10px"} maxH={"452px"} overflowY={"auto"}>
                      {orderRuleFileList.length > 0 && (
                        <Flex
                          flexDirection={"column"}
                          gap={"4px"}
                          marginBottom={"8px"}
                        >
                          {orderRuleFileList.map((file) => {
                            const fileType =
                              file.name
                                .toLowerCase()
                                .split(".")
                                .at(1)
                                ?.toLowerCase() ?? "default";
                            return (
                              <Flex
                                key={file.id}
                                alignItems={"center"}
                                gap={"6px"}
                                padding={"6px"}
                                borderRadius={"4px"}
                                border={"0.5px solid #D4D4D1"}
                                background={"#FCFCFE"}
                              >
                                <Box flexShrink={0}>
                                  <SVG
                                    src={
                                      fileIconsByType[fileType] ??
                                      fileIconsByType.default
                                    }
                                    width={"16px"}
                                    height={"16px"}
                                  />
                                </Box>
                                <Text
                                  fontSize={"11px"}
                                  color={"#444444"}
                                  flexGrow={1}
                                  overflow={"hidden"}
                                  textOverflow={"ellipsis"}
                                  whiteSpace={"nowrap"}
                                >
                                  {file.name}
                                </Text>
                                <Box
                                  flexShrink={0}
                                  cursor={"pointer"}
                                  onClick={() => {
                                    fireDownloadUnoFile(
                                      { unoFileId: file.id },
                                      {}
                                    );
                                  }}
                                >
                                  <SVG
                                    src={"/icons-v1_1/download.svg"}
                                    width={"14px"}
                                    height={"14px"}
                                  />
                                </Box>
                              </Flex>
                            );
                          })}
                        </Flex>
                      )}
                      {(orderRulesText?.trim() ?? "") !== "" ? (
                        <Text
                          fontSize={"11px"}
                          whiteSpace={"pre-wrap"}
                          lineHeight={1.6}
                          color={"#444444"}
                        >
                          {orderRulesText}
                        </Text>
                      ) : (
                        orderRuleFileList.length === 0 && (
                          <Text fontSize={"11px"} color={"#8F8F8C"}>
                            -
                          </Text>
                        )
                      )}
                    </Box>
                  )}
                </Box>
              )}
              <Flex flexDirection={"column"} gap={"12px"} flex={1} minW={0}>
                {/* 파싱/반영 버튼 */}
                <Flex alignItems={"center"} gap={"12px"}>
                  <Text fontSize={"12px"} color={"#6F6F6C"}>
                    {tTitle("brand")}
                  </Text>
                  <Spacer />
                  <AppButton
                    variant={"normal"}
                    chakraProps={{ p: "4px 10px", flexShrink: 0 }}
                    isLoading={isParsing}
                    isDisabled={isReflecting}
                    onClick={handleClickParseButton}
                  >
                    <Text fontSize={"11px"} fontWeight={"700"}>
                      {tTitle("parse_order_rules")}
                    </Text>
                  </AppButton>
                  <AppButton
                    variant={"normal"}
                    chakraProps={{ p: "4px 10px", flexShrink: 0 }}
                    isLoading={isReflecting}
                    isDisabled={matchedRulesByBrandId.size === 0 || isParsing}
                    onClick={() => void handleReflect()}
                  >
                    <Text fontSize={"11px"} fontWeight={"700"}>
                      {tTitle("reflect_order_rules")}
                    </Text>
                  </AppButton>
                </Flex>

                {/* 전체 같은 값 입력 */}
                <Flex
                  alignItems={"center"}
                  gap={"8px"}
                  padding={"8px 12px"}
                  background={"#F7F9FA"}
                  borderRadius={"6px"}
                  border={"0.5px solid #D4D4D1"}
                >
                  <Text fontSize={"12px"} fontWeight={"700"} width={"100px"}>
                    {tTitle("all")}
                  </Text>
                  <Box width={"120px"}>
                    <PriceAdjustedByTypeSelect
                      width={"120px"}
                      value={bulkBy}
                      onSelect={(v) => {
                        if (v) setBulkBy(v);
                      }}
                    />
                  </Box>
                  <Input
                    width={"80px"}
                    type={"number"}
                    fontSize={"12px"}
                    height={"28px"}
                    padding={"0 8px"}
                    borderColor={"#D4D4D1"}
                    background={"#FFFFFF"}
                    _hover={{ borderColor: "#D4D4D1" }}
                    _focus={{ borderColor: "#8F8F8C", boxShadow: "none" }}
                    value={bulkValue}
                    onChange={(e) => setBulkValue(e.target.value)}
                  />
                  <AppButton
                    variant={"normal"}
                    onClick={handleApplyToAll}
                    isDisabled={rows.length === 0}
                  >
                    <Text fontSize={"12px"}>{tTitle("apply")}</Text>
                  </AppButton>
                </Flex>

                {/* 테이블 */}
                <Box
                  border={"0.5px solid #D4D4D1"}
                  borderRadius={"6px"}
                  overflow={"hidden"}
                  background={"#FFFFFF"}
                >
                  {/* 헤더 */}
                  <Flex
                    alignItems={"center"}
                    gap={"8px"}
                    padding={"8px 14px"}
                    fontSize={"12px"}
                    fontWeight={"600"}
                    color={"#6F6F6C"}
                    background={"#F7F9FA"}
                    borderBottom={"0.5px solid #E8E8E5"}
                  >
                    <Box>{tTitle("brand")}</Box>
                    <Spacer />
                    <Box width={"120px"}>{tTitle("condition")}</Box>
                    <Box width={"80px"} textAlign={"right"}>
                      {tTitle("value")}
                    </Box>
                  </Flex>

                  {/* 대상별 입력 */}
                  {isLoading ? (
                    <Flex
                      padding={"24px"}
                      justifyContent={"center"}
                      alignItems={"center"}
                    >
                      <Spinner size={"sm"} />
                    </Flex>
                  ) : rows.length === 0 ? (
                    <Box padding={"16px 14px"}>
                      <Text fontSize={"12px"} color={"#8F8F8C"}>
                        {tTitle("no_data")}
                      </Text>
                    </Box>
                  ) : (
                    <Box maxH={"360px"} overflowY={"auto"}>
                      {rows.map((row, idx) => {
                        const matchedRule = matchedRulesByBrandId.get(
                          row.brandId
                        );
                        const productCount =
                          productConditionMap.get(row.brandId)?.size ?? 0;
                        const hasProductConditions = productCount > 0;
                        return (
                          <Flex
                            key={row.brandId}
                            alignItems={"center"}
                            gap={"8px"}
                            padding={"8px 14px"}
                            borderTop={
                              idx === 0 ? "none" : "0.5px solid #F0F0ED"
                            }
                            transition={"background-color 0.12s"}
                            _hover={{ background: "#FAFAFA" }}
                          >
                            <Flex
                              flex={1}
                              minW={0}
                              alignItems={"center"}
                              gap={"8px"}
                            >
                              <Flex
                                flexDirection={"column"}
                                flex={1}
                                minW={0}
                                gap={"3px"}
                              >
                                <Text
                                  fontSize={"12px"}
                                  color={"#444444"}
                                  overflow={"hidden"}
                                  textOverflow={"ellipsis"}
                                  whiteSpace={"nowrap"}
                                >
                                  {row.brandName}
                                </Text>
                                {matchedRule &&
                                  matchedRule.conditions.length > 0 && (
                                    <Flex flexWrap={"wrap"} gap={"4px"}>
                                      {matchedRule.conditions.map((c, ci) => (
                                        <Text
                                          key={ci}
                                          fontSize={"10px"}
                                          color={"#7A6A00"}
                                          background={"#FFF7CC"}
                                          border={"0.5px solid #E5DCA0"}
                                          borderRadius={"3px"}
                                          padding={"1px 6px"}
                                          whiteSpace={"nowrap"}
                                        >
                                          {formatMatchedConditionLabel(c)}
                                        </Text>
                                      ))}
                                    </Flex>
                                  )}
                              </Flex>
                              <AppButton
                                variant={"normal"}
                                chakraProps={{ p: "2px 8px", flexShrink: 0 }}
                                onClick={() => {
                                  setProductModalBrand({
                                    id: row.brandId,
                                    name: row.brandName,
                                  });
                                }}
                              >
                                <Text fontSize={"11px"}>
                                  {tTitle("apply_per_product")}
                                  {productCount > 0 ? ` (${productCount})` : ""}
                                </Text>
                              </AppButton>
                            </Flex>
                            <Box width={"120px"} flexShrink={0}>
                              <PriceAdjustedByTypeSelect
                                width={"120px"}
                                value={row.by}
                                isDisabled={hasProductConditions}
                                onSelect={(v) => {
                                  if (v) handleChangeRow(idx, { by: v });
                                }}
                              />
                            </Box>
                            <Input
                              width={"80px"}
                              flexShrink={0}
                              type={"number"}
                              fontSize={"12px"}
                              height={"28px"}
                              padding={"0 8px"}
                              textAlign={"right"}
                              borderColor={"#D4D4D1"}
                              background={
                                hasProductConditions ? "#F7F9FA" : "#FFFFFF"
                              }
                              color={
                                hasProductConditions ? "#6F6F6C" : undefined
                              }
                              isDisabled={hasProductConditions}
                              _disabled={{ cursor: "not-allowed", opacity: 1 }}
                              _hover={{ borderColor: "#D4D4D1" }}
                              _focus={{
                                borderColor: "#8F8F8C",
                                boxShadow: "none",
                              }}
                              value={row.value}
                              readOnly={hasProductConditions}
                              onChange={(e) =>
                                handleChangeRow(idx, { value: e.target.value })
                              }
                            />
                          </Flex>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Flex>
            </Flex>
          </ModalBody>
          <ModalFooter display={"flex"} gap={"12px"}>
            <AppButton onClick={onClose}>{tTitle("not_now")}</AppButton>
            <AppButton variant={"primary"} onClick={handleConfirm}>
              {tTitle("apply")}
            </AppButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 파싱 버튼 클릭 시: 저장된 파싱 결과 적용 / 새로 파싱 선택 */}
      <OrderRuleParseChoiceModal
        isOpen={isParseChoiceOpen}
        onClose={() => setIsParseChoiceOpen(false)}
        stored={parseChoiceStored}
        orderRuleFiles={orderRuleFiles}
        onApplyStored={applyStoredParseResult}
        onReparse={() => {
          setIsParseChoiceOpen(false);
          void handleParseOrderRules(parseChoiceFileId ?? undefined);
        }}
      />

      {/* 오더룰 파일이 여러개일 때: 어떤 파일로 파싱할지 선택 */}
      <Modal
        isOpen={isFileSelectOpen}
        onClose={() => setIsFileSelectOpen(false)}
        size={"md"}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader
            color={"#444440"}
            fontSize={"14px"}
            background={"#F7F9FA"}
            borderBottom={"0.5px solid #8F8F8C"}
          >
            {tTitle("select_order_rule_file")}
          </ModalHeader>
          <ModalBody>
            <Text fontSize={"12px"} color={"#6F6F6C"} marginBottom={"12px"}>
              {tTitle("select_order_rule_file_description")}
            </Text>
            <RadioGroup
              value={selectedFileId !== null ? String(selectedFileId) : ""}
              onChange={(v) => setSelectedFileId(Number(v))}
            >
              <Flex flexDirection={"column"} gap={"8px"}>
                {orderRuleFileList.map((file) => {
                  const fileStored = getStoredParseResult(file.id);
                  return (
                    <Box
                      key={file.id}
                      padding={"8px 10px"}
                      borderRadius={"6px"}
                      border={"0.5px solid #D4D4D1"}
                      background={
                        selectedFileId === file.id ? "#F0F6FF" : "#FCFCFE"
                      }
                      cursor={"pointer"}
                      onClick={() => setSelectedFileId(file.id)}
                    >
                      <Radio value={String(file.id)} alignItems={"center"}>
                        <Flex alignItems={"center"} gap={"6px"} minW={0}>
                          <Text
                            fontSize={"12px"}
                            color={"#444444"}
                            overflow={"hidden"}
                            textOverflow={"ellipsis"}
                            whiteSpace={"nowrap"}
                          >
                            {file.name}
                          </Text>
                          {fileStored && (
                            <Text
                              flexShrink={0}
                              fontSize={"10px"}
                              color={"#7A6A00"}
                              background={"#FFF7CC"}
                              border={"0.5px solid #E5DCA0"}
                              borderRadius={"3px"}
                              padding={"1px 6px"}
                              whiteSpace={"nowrap"}
                            >
                              {tTitle("already_parsed")}
                            </Text>
                          )}
                        </Flex>
                      </Radio>
                    </Box>
                  );
                })}
              </Flex>
            </RadioGroup>
          </ModalBody>
          <ModalFooter display={"flex"} gap={"12px"}>
            <AppButton onClick={() => setIsFileSelectOpen(false)}>
              {tTitle("cancel")}
            </AppButton>
            <AppButton
              variant={"primary"}
              isDisabled={selectedFileId === null}
              onClick={() => {
                if (selectedFileId === null) return;
                setIsFileSelectOpen(false);
                // 선택한 파일에 저장 결과가 있으면 선택 모달, 없으면 바로 파싱.
                maybeShowChoiceOrParse(selectedFileId);
              }}
            >
              {tTitle("apply")}
            </AppButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 브랜드 행의 "상품별 적용" — 대상(라인 시트/시즌) + 브랜드로 스코프 */}
      {productModalBrand && (
        <ApplyConditionProductModal
          isOpen={productModalBrand !== null}
          onClose={() => setProductModalBrand(null)}
          lineSheetSetId={lineSheetSetId}
          target={
            target.kind === "LINE_SHEET"
              ? {
                  kind: "LINE_SHEET_BRAND",
                  lineSheetId: target.id,
                  brandId: productModalBrand.id,
                  name: productModalBrand.name,
                }
              : {
                  kind: "SEASON_BRAND",
                  seasonName: target.seasonName,
                  brandId: productModalBrand.id,
                  name: productModalBrand.name,
                }
          }
          initialEntries={productConditionMap.get(productModalBrand.id)}
          matchedConditions={
            matchedRulesByBrandId.get(productModalBrand.id)?.conditions
          }
          orderRulesText={orderRulesText}
          orderRuleFiles={orderRuleFiles}
          onConfirm={(entries) => {
            const brandId = productModalBrand.id;
            setProductConditionMap((prev) => {
              const next = new Map(prev);
              const sub = new Map(next.get(brandId) ?? []);
              entries.forEach((e) => {
                if (e.value.trim() === "") {
                  sub.delete(e.productId);
                } else {
                  sub.set(e.productId, { by: e.by, value: e.value });
                }
              });
              if (sub.size === 0) {
                next.delete(brandId);
              } else {
                next.set(brandId, sub);
              }
              return next;
            });
          }}
        />
      )}
    </>
  );
}
